// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import styles from '@app/features/auth/components/modals/SudoVerificationModal.module.css';
import SudoPrompt, {SudoVerificationMethod} from '@app/features/auth/state/SudoPrompt';
import * as WebAuthnUtils from '@app/features/auth/utils/WebAuthnUtils';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import * as FormUtils from '@app/lib/forms';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';

const logger = new Logger('SudoVerificationModal');

interface FormInputs {
	password: string;
	totpCode: string;
}

const isMacAppIdentifierError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : '';
	return message.toLowerCase().includes('application identifier');
};

const SudoVerificationModal: React.FC = observer(() => {
	const {t, i18n} = useLingui();
	const {availableMethods, isVerifying, verificationError, rawError, lastUsedMfaMethod} = SudoPrompt;
	const form = useForm<FormInputs>({defaultValues: {password: '', totpCode: ''}});
	const [webAuthnInFlight, setWebAuthnInFlight] = useState(false);
	const [webAuthnError, setWebAuthnError] = useState<string | null>(null);
	const autoTriggeredRef = useRef(false);

	const showPasskey = availableMethods.webauthn;
	const showTotp = availableMethods.totp;
	const showPassword = availableMethods.password;
	const noMethodsAvailable = !showPasskey && !showTotp && !showPassword;
	const showDivider = showPasskey && (showTotp || showPassword);

	useEffect(() => {
		form.reset({password: '', totpCode: ''});
		setWebAuthnError(null);
		setWebAuthnInFlight(false);
		autoTriggeredRef.current = false;
	}, [form]);

	useEffect(() => {
		if (!verificationError && !rawError) return;
		const fallback: keyof FormInputs = showPassword ? 'password' : showTotp ? 'totpCode' : 'password';
		if (rawError) {
			FormUtils.handleError(i18n, form, rawError, fallback);
		} else if (verificationError) {
			form.setError(fallback, {type: 'server', message: verificationError});
		}
		setWebAuthnInFlight(false);
	}, [form, verificationError, rawError, i18n, showPassword, showTotp]);

	const handleWebAuthn = async () => {
		if (webAuthnInFlight || isVerifying) return;
		setWebAuthnError(null);
		form.clearErrors();
		setWebAuthnInFlight(true);
		try {
			await WebAuthnUtils.assertWebAuthnSupported();
			const optionsResponse = await conduit.post<{challenge: string}>(Endpoints.SUDO_WEBAUTHN_OPTIONS);
			const credential = await WebAuthnUtils.performAuthentication(optionsResponse.body);
			SudoPrompt.submit({
				mfa_method: SudoVerificationMethod.WEBAUTHN,
				webauthn_challenge: optionsResponse.body.challenge,
				webauthn_response: credential,
			});
		} catch (err) {
			logger.error('WebAuthn verification failed', err);
			setWebAuthnInFlight(false);
			if (isMacAppIdentifierError(err)) {
				setWebAuthnError(
					t`Passkeys require a signed macOS bundle with a valid application identifier. Please install the signed desktop client and retry.`,
				);
				return;
			}
			setWebAuthnError(t`Couldn't verify with passkey. Please try again.`);
		}
	};

	useEffect(() => {
		if (autoTriggeredRef.current) return;
		if (!showPasskey || showPassword || showTotp) return;
		if (lastUsedMfaMethod && lastUsedMfaMethod !== 'webauthn') return;
		autoTriggeredRef.current = true;
		void handleWebAuthn();
	}, [showPasskey, showPassword, showTotp]);

	const handleClose = () => {
		SudoPrompt.reject(new DOMException('User cancelled verification', 'AbortError'));
	};

	const onSubmit = (values: FormInputs) => {
		form.clearErrors();
		if (showTotp && values.totpCode) {
			SudoPrompt.submit({mfa_method: SudoVerificationMethod.TOTP, mfa_code: values.totpCode});
			return;
		}
		if (showPassword && values.password) {
			SudoPrompt.submit({password: values.password});
			return;
		}
		if (showPasskey && !showTotp && !showPassword) {
			void handleWebAuthn();
			return;
		}
		const target: keyof FormInputs = showPassword ? 'password' : 'totpCode';
		form.setError(target, {
			type: 'manual',
			message: showPassword ? t`Enter your password.` : t`Enter the code from your authenticator app.`,
		});
	};

	return (
		<Modal.Root size="small" centered onClose={handleClose}>
			<Form form={form} onSubmit={onSubmit} aria-label={t`Verify identity form`}>
				<Modal.Header title={t`Verify it's you`} onClose={handleClose} />
				<Modal.Content>
					<div className={styles.container}>
						<p className={styles.description}>
							<Trans>For your security, please confirm it's you to continue.</Trans>
						</p>

						{noMethodsAvailable ? (
							<div className={styles.unavailable} role="alert">
								<p className={styles.unavailableTitle}>
									<Trans>No verification methods available</Trans>
								</p>
								<p className={styles.unavailableBody}>
									<Trans>
										Your account requires multi-factor authentication, but no supported methods are set up on this
										device. Please sign in on a device with your authenticator app or security key, or contact support.
									</Trans>
								</p>
							</div>
						) : (
							<>
								{showPasskey && (
									<>
										{webAuthnInFlight ? (
											<div className={styles.passkeyVerifying} role="status">
												<Spinner />
												<span>
													<Trans>Waiting for passkey…</Trans>
												</span>
											</div>
										) : (
											<Button
												type="button"
												onClick={handleWebAuthn}
												disabled={isVerifying}
												fitContainer
												autoFocus={!showTotp && !showPassword}
											>
												<Trans>Continue with passkey</Trans>
											</Button>
										)}
										{webAuthnError && (
											<p className={styles.formError} role="alert">
												{webAuthnError}
											</p>
										)}
									</>
								)}

								{showDivider && (
									<div className={styles.divider} aria-hidden>
										<Trans>or</Trans>
									</div>
								)}

								{showTotp && (
									<Input
										{...form.register('totpCode')}
										label={t`Authenticator code`}
										placeholder={t`6-digit code`}
										autoComplete="one-time-code"
										inputMode="numeric"
										autoFocus={!showPasskey || lastUsedMfaMethod === 'totp'}
										error={form.formState.errors.totpCode?.message}
									/>
								)}

								{showPassword && (
									<Input
										{...form.register('password')}
										label={t`Password`}
										type="password"
										autoFocus={!showPasskey}
										error={form.formState.errors.password?.message}
									/>
								)}
							</>
						)}
					</div>
				</Modal.Content>
				<Modal.Footer>
					<Button type="button" variant="secondary" onClick={handleClose} disabled={isVerifying}>
						<Trans>Cancel</Trans>
					</Button>
					{!noMethodsAvailable && (showTotp || showPassword) && (
						<Button type="submit" submitting={isVerifying} disabled={isVerifying || webAuthnInFlight}>
							<Trans>Verify</Trans>
						</Button>
					)}
				</Modal.Footer>
			</Form>
		</Modal.Root>
	);
});

export default SudoVerificationModal;
